import React, { useEffect, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, StarIcon, DownloadIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
export function HeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cartUrl = 'https://competirsinmiedo.com/cart/add?id=45092891263029&quantity=1';
  // Defer video loading until after first paint
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Small delay to let critical content render first
    const timer = setTimeout(() => {
      video.src = 'https://cdn.magicpatterns.com/uploads/b6ssMGLKuoeiXiQFBmiu3j/copy_A61BCB96-EA7B-4CF1-89CB-D97AE75A0533.mov';
      video.load();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  return <section className="relative pt-12 pb-16 md:pt-24 md:pb-32 overflow-hidden" data-id="element-66">
      {/* Video Background - deferred loading */}
      <video ref={videoRef} autoPlay loop muted playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" data-id="element-67" />

      {/* Overlay to ensure text readability */}
      <div className="absolute inset-0 bg-black/40 z-0" data-id="element-68"></div>

      {/* Background decorative elements - hidden on mobile for performance */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-brand-green/20 blur-3xl z-0 hidden md:block" data-id="element-69"></div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-brand-amber/20 blur-3xl z-0 hidden md:block" data-id="element-70"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-71">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-72">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-73">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white font-medium mb-2 border border-white/20 text-[11px]" data-id="element-74">
              <StarIcon className="inline w-4 h-4 fill-[#65A30D] text-[#65A30D] mr-1.5" data-id="element-75" />
              Para familias y futbolistas jóvenes
            </div>
            <p className="text-white/70 italic mb-6 text-[11px]" data-id="element-76">
              Porque creemos en la pasión del deporte y en la fortaleza del
              poder juvenil
            </p>

            <p className="md:text-xl text-[#65A30D] mb-3 text-center uppercase tracking-wide text-[24px] font-[800]" data-id="element-77"></p>

            <h1 className="md:text-5xl lg:text-6xl font-heading text-white mb-6 font-[600] text-[42px]" data-id="element-78">
              Ayuda a tu hijo a jugar sin miedo y volver a disfrutar del fútbol{' '}
              <span className="text-[#65A30D]" data-id="element-79">en 14 días</span>
            </h1>

            <p className="md:text-xl text-white/80 mb-8 text-[13px]" data-id="element-80">
              Si tu hijo entrena bien, pero en los partidos se bloquea, pierde
              confianza o juega con miedo al error, esta guía fue creada para
              ayudarlo a volver a competir con seguridad.
            </p>

            <div className="flex flex-col items-center lg:items-start space-y-4" data-id="element-81">
              <p className="text-red-500 font-bold tracking-wide uppercase text-[15px]" data-id="element-82">
                ¡AYÚDALO ANTES DE SU PRÓXIMO JUEGO!
              </p>

              {/* Trust Badges */}
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-white/80 mt-4" data-id="element-83">
                <div className="flex items-center" data-id="element-84">
                  <ShieldCheckIcon className="w-4 h-4 text-brand-green-light mr-1" data-id="element-85" />
                  <span className="text-[11px]" data-id="element-86">Pago Seguro</span>
                </div>
                <div className="flex items-center" data-id="element-87">
                  <DownloadIcon className="w-4 h-4 text-brand-green-light mr-1" data-id="element-88" />
                  <span className="text-[11px]" data-id="element-89">
                    Acceso Inmediato al E-Book
                  </span>
                </div>
                <div className="flex items-center" data-id="element-90">
                  <div className="flex mr-1" data-id="element-91">
                    {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-4 h-4 fill-brand-amber text-brand-amber" data-id="element-92" />)}
                  </div>
                  <span className="text-[12px]" data-id="element-93">
                    +3,200 Familias con resultados
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Product Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative mx-auto w-full max-w-md" data-id="element-94">
            <img src="https://cdn.magicpatterns.com/uploads/9JDoySjskcLgTNnRbeBdQK/WhatsApp_Image_2026-03-31_at_00.12.25.jpg" alt="Competir Sin Miedo — Programa de fortaleza mental para futbolistas y familias" className="w-full h-auto drop-shadow-2xl rounded-sm" loading="lazy" data-id="element-95" />

            {/* Decorative elements around book */}
            <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-white p-3 sm:p-4 rounded-xl shadow-soft animate-bounce-slow" data-id="element-96">
              <div className="text-brand-amber font-bold text-lg sm:text-xl" data-id="element-97">
                +9 Bonos
              </div>
              <div className="text-xs text-brand-slate-light" data-id="element-98">
                ¡Incluidos HOY!
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}